/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { type AxiosResponse } from 'axios';
import filter from 'lodash/filter';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import { calendarDateToUtcString, LoggingHelper as logging } from 'sarsaparilla';
import { parseCampsiteFilters } from 'ui-search/src/utils/filters';

import type { CalendarDate, DateValue } from '@internationalized/date';
import { startOfMonth } from 'date-fns';
import { type Dispatch } from 'redux';
import * as InternalSecurityUtils from 'ui-internal-account/src/utilities/internalSecurityUtil';
import * as searchActions from 'ui-search/src/actions/search';
import type { NextAvailableResult } from '../../../types/camping';
import * as types from '../constants';
import { getAvailMonthsFromStartingMonth } from '../utils/dateUtils';
import { campingSearchAxios } from '../utils/externalAxios';
import * as utils from '../utils/utils';

// fetch doesn't handle errors well
const status = (response: AxiosResponse) => {
    if (response.status >= 200 && response.status < 300) {
        return Promise.resolve(response);
    }
    logging.logError(response.statusText);
    return Promise.reject(new Error(response.statusText));
};

const data = (response: AxiosResponse) => response.data;

const campsitesFailed = (message: string) => ({
    type: types.GET_AVAILABLE_CAMPSITES_ERROR,
    payload: message,
    error: true,
});

const v3CampsitesFailed = (message: string) => ({
    type: types.GET_V3_AVAILABLE_CAMPSITES_ERROR,
    payload: message,
    error: true,
});

// fetch the number of unfiltered campsites
export const fetchUnfilteredCampsitesCount =
    (campgroundId: string, customFilters = {}, isInternal = false) =>
    (dispatch: Dispatch<any>) => {
        const baseFilters = [
            {
                attribute: 'asset_id',
                values: [
                    {
                        value: campgroundId,
                    },
                ],
            },
        ];
        const args: {
            start: number;
            size: number;
            fq: any[];
            include_non_site_specific_campsites: boolean;
            fg?: any;
        } = {
            start: 0,
            size: 1000,
            fq: searchActions.buildSearchFilters(baseFilters, []),
            include_non_site_specific_campsites: true,
        };

        let url;
        let headers = {};
        if (isInternal) {
            url = utils.urlWithParams(`/internal/campsites`, args);
            headers = {
                headers: {
                    Authorization: InternalSecurityUtils.getAuthHeader(),
                },
            };
        } else {
            url = utils.urlWithParams(`${process.env.API}/search/campsites`, args);
        }

        return campingSearchAxios
            .get(url, headers)
            .then(status)
            .then(data)
            .then((json) => {
                let campsites = json.campsites ? json.campsites : [];

                if (!isEmpty(customFilters)) {
                    if (has(customFilters, 'management')) {
                        campsites = filter(campsites, (c) => {
                            return !includes(c.type, types.CAMPSITE_TYPES.MANAGEMENT);
                        });
                    }
                }

                const payload = {
                    totalCount: campsites.length,
                };

                dispatch({
                    type: types.GET_UNFILTERED_CAMPSITES_COUNT,
                    payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(campsitesFailed('Campsites unavailable at this time.'));
                }
            });
    };

// fetch campsites for a campground, including currently selected search criteria

export const fetchCampsites =
    (
        campgroundId: string,
        isApplyFilters: boolean,
        customFilters = {},
        isInternal = false
    ) =>
    (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
        const state = getState();

        // add search filter by campground id
        const baseFilters = [
            {
                attribute: 'asset_id',
                values: [
                    {
                        value: campgroundId,
                    },
                ],
            },
        ];

        // create search filters based on latest state of search uiFilters
        // note: there could be more filters in search state, but we'll include only those specific to campsites
        let uiFilters = null;
        if (isApplyFilters) {
            uiFilters = {
                campsiteTypes: state.search.uiFilters.campsiteTypes,
                campsiteUseTypes: state.search.uiFilters.campsiteUseTypes,
                campsiteAmenities: state.search.uiFilters.campsiteAmenities,
                campsiteElectricityHookup:
                    state.search.uiFilters.campsiteElectricityHookup,
                campsiteVehicleLength: state.search.uiFilters.campsiteVehicleLength,
                campsiteEquipment: state.search.uiFilters.campsiteEquipment,
                occupants: state.search.uiFilters.occupants,
                reservable: state.search.uiFilters.reservable,
            };
        }

        const args: {
            start: number;
            size: number;
            fq: any[];
            include_non_site_specific_campsites: boolean;
            fg?: string[];
        } = {
            start: 0,
            size: 1000,
            fq: searchActions.buildSearchFilters(baseFilters, uiFilters),
            include_non_site_specific_campsites: true,
        };

        if (state.search.fg) {
            const parsedFgParams = parseCampsiteFilters(state.search.fg);
            args.fg = parsedFgParams;
        }

        let url;
        let headers = {};
        if (isInternal) {
            url = utils.urlWithParams(
                `${process.env.API}/search/internal/campsites`,
                args
            );
            headers = {
                headers: {
                    Authorization: InternalSecurityUtils.getAuthHeader(),
                },
            };
        } else {
            url = utils.urlWithParams(`${process.env.API}/search/campsites`, args);
        }
        dispatch({ type: types.FETCHING_CAMPSITES_START });
        return campingSearchAxios
            .get(url, headers)
            .then(status)
            .then(data)
            .then((json) => {
                let campsites = json.campsites ? json.campsites : [];
                if (!isEmpty(customFilters) && has(customFilters, 'management')) {
                    campsites = filter(campsites, (c) => {
                        return !includes(c.type, types.CAMPSITE_TYPES.MANAGEMENT);
                    });
                }
                const payload = {
                    campground_id: campgroundId,
                    campsites,
                };
                dispatch({ type: types.GET_AVAILABLE_CAMPSITES, payload });
                return Promise.resolve(payload);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(campsitesFailed('Campsites unavailable at this time.'));
                }
            });
    };

// can specify either monthsToSearch or startDate and endDate
interface V3FetchCampsitesParams {
    monthsToSearch?: number;
    startDate?: DateValue;
    endDate?: DateValue;
}

export const fetchNextAvailableDates =
    (campgroundId: string, isApplyFilters: boolean, start_date?: CalendarDate) =>
    (_dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
        const state = getState();
        let uiFilters = null;

        if (isApplyFilters) {
            uiFilters = {
                campsiteTypes: state.search.uiFilters.campsiteTypes,
                campsiteUseTypes: state.search.uiFilters.campsiteUseTypes,
                campsiteAmenities: state.search.uiFilters.campsiteAmenities,
                campsiteElectricityHookup:
                    state.search.uiFilters.campsiteElectricityHookup,
                campsiteVehicleLength: state.search.uiFilters.campsiteVehicleLength,
                campsiteEquipment: state.search.uiFilters.campsiteEquipment,
                occupants: state.search.uiFilters.occupants,
                reservable: state.search.uiFilters.reservable,
            };
        }

        const args: {
            asset_id: string;
            fq: string[];
            fg?: string[];
            start_date?: string;
        } = {
            asset_id: campgroundId,
            fq: searchActions.buildSearchFilters([], uiFilters),
            ...(start_date ? { start_date: calendarDateToUtcString(start_date) } : {}),
        };

        if (state.search.fg) {
            args.fg = parseCampsiteFilters(state.search.fg);
        }

        const url = utils.urlWithParams(
            `${process.env.API}/search/campsites/next_available`,
            args
        );

        return campingSearchAxios
            .get(url)
            .then(status)
            .then(data) as Promise<NextAvailableResult>;
    };

export const v3FetchCampsites =
    (
        campgroundId: string,
        isApplyFilters: boolean,
        customFilters = {},
        params: V3FetchCampsitesParams
    ) =>
    (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
        const { monthsToSearch, startDate, endDate } = params;

        const state = getState();

        // add search filter by campground id
        const baseFilters: { attribute: string; values: { value: string }[] }[] = [];

        // create search filters based on latest state of search uiFilters
        // note: there could be more filters in search state, but we'll include only those specific to campsites
        let uiFilters = null;
        if (isApplyFilters) {
            uiFilters = {
                campsiteTypes: state.search.uiFilters.campsiteTypes,
                campsiteUseTypes: state.search.uiFilters.campsiteUseTypes,
                campsiteAmenities: state.search.uiFilters.campsiteAmenities,
                campsiteElectricityHookup:
                    state.search.uiFilters.campsiteElectricityHookup,
                campsiteVehicleLength: state.search.uiFilters.campsiteVehicleLength,
                campsiteEquipment: state.search.uiFilters.campsiteEquipment,
                occupants: state.search.uiFilters.occupants,
                reservable: state.search.uiFilters.reservable,
            };
        }

        const startingMonth = startOfMonth(new Date());

        const args: {
            asset_id: string;
            start: number;
            size: number;
            fq: any[];
            include_non_site_specific_campsites: boolean;
            fg?: string[];
            avail_months?: string[];
            avail_nights?: number;
            sort?: string;
        } = {
            asset_id: campgroundId,
            start: 0,
            size: 0,
            fq: searchActions.buildSearchFilters(baseFilters, uiFilters),
            include_non_site_specific_campsites: true,
            sort: 'available',
            ...(monthsToSearch
                ? {
                      avail_months: getAvailMonthsFromStartingMonth(
                          startingMonth,
                          monthsToSearch
                      ),
                  }
                : {}),
            ...(monthsToSearch ? { avail_nights: 1 } : {}),
            ...(startDate ? { start_date: calendarDateToUtcString(startDate) } : {}),
            ...(endDate ? { end_date: calendarDateToUtcString(endDate) } : {}),
        };

        if (state.search.fg) {
            const parsedFgParams = parseCampsiteFilters(state.search.fg);
            args.fg = parsedFgParams;
        }

        const url = utils.urlWithParams(
            `${process.env.API}/search/campsites/availability`,
            args
        );
        dispatch({ type: types.FETCHING_V3_CAMPSITES_START });
        return campingSearchAxios
            .get(url)
            .then(status)
            .then(data)
            .then((json) => {
                let campsites = json.campsites ? json.campsites : [];
                if (!isEmpty(customFilters) && has(customFilters, 'management')) {
                    campsites = filter(campsites, (c) => {
                        return !includes(c.type, types.CAMPSITE_TYPES.MANAGEMENT);
                    });
                }
                const payload = {
                    campground_id: campgroundId,
                    campsites,
                };
                dispatch({ type: types.GET_V3_AVAILABLE_CAMPSITES, payload });
                return Promise.resolve(payload);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(v3CampsitesFailed('Campsites unavailable at this time.'));
                }
            });
    };

export const setV3FlexibleSearchParams = (
    months: Set<string>,
    nights: number,
    weekendsOnly: boolean
) => ({
    type: types.SET_V3_FLEXIBLE_SEARCH_PARAMS,
    payload: {
        months: Array.from(months).sort(),
        nights,
        weekendsOnly,
    },
});

export const clearV3FlexibleSearchParams = () => ({
    type: types.CLEAR_V3_FLEXIBLE_SEARCH_PARAMS,
});

export const v3fetchFlexibleCampsites =
    (
        campgroundId: string,
        monthKeys: Set<string>,
        nights: number,
        weekendsOnly: boolean
    ) =>
    (dispatch: Dispatch<any>) => {
        dispatch(setV3FlexibleSearchParams(monthKeys, nights, weekendsOnly));

        const availMonths = Array.from(monthKeys)
            .sort()
            .map((key) => `${key}-01T00:00:00Z`);

        const args = {
            asset_id: campgroundId,
            avail_nights: nights,
            size: 1000,
            agg: 'type',
            avail_months: availMonths,
            include_non_site_specific_campsites: true,
            ...(weekendsOnly ? { avail_includes_weekend: true } : {}),
            sort: 'available',
        };

        const headers = {};

        const url = utils.urlWithParams(
            `${process.env.API}/search/campsites/availability`,
            args
        );

        dispatch({ type: types.FETCHING_V3_CAMPSITES_START });
        return campingSearchAxios
            .get(url, headers)
            .then(status)
            .then(data)
            .then((json) => {
                const campsites = json.campsites ? json.campsites : [];
                const payload = {
                    campground_id: campgroundId,
                    campsites,
                };
                dispatch({ type: types.GET_V3_AVAILABLE_CAMPSITES, payload });
                return Promise.resolve(payload);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(v3CampsitesFailed('Campsites unavailable at this time.'));
                }
            });
    };
